import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  SentimentApiCommentsPage,
  SentimentApiCustomerRankingItem,
  SentimentApiMentionsPage,
  SentimentApiRankingItem,
  SentimentApiResponse,
  SentimentApiScope,
  SentimentApiSummary,
  SentimentApiTaxonomyItem,
  SentimentChildRankingParams,
  SentimentCommentsParams,
  SentimentCrossParams,
  SentimentRankingParams,
  SentimentReportParams,
  SentimentReportResponse,
  SentimentSummaryParams,
} from '../models/sentiment-api.model';
import { SentimentTaxonomyKind } from '../models/sentiment-filter.model';

/**
 * Lo único que separa a las dos vistas: el prefijo de ruta y el nombre del
 * parámetro con que viajan los hoteles. El resto de los endpoints —sufijo,
 * filtros y forma de la respuesta— es idéntico.
 */
const SCOPE_CONFIG: Record<SentimentApiScope, { prefix: string; customerParam: string }> = {
  property: { prefix: '', customerParam: 'customer_id' },
  chain: { prefix: 'corporative/', customerParam: 'customers_ids' },
};

@Injectable({
  providedIn: 'root',
})
export class SentimentService {
  private readonly http = inject(HttpClient);

  /** Alimenta las cuatro tarjetas de resumen con una sola llamada. */
  getSummary(params: SentimentSummaryParams, scope: SentimentApiScope): Observable<SentimentApiSummary> {
    return this.http
      .get<SentimentApiResponse<SentimentApiSummary>>(this.url('summary', scope), {
        params: this.buildParams(params, scope),
      })
      .pipe(map((response) => response.data));
  }

  /** Listado del panel de tendencia, según la pestaña activa (temas, áreas o dimensiones). */
  getRankings(params: SentimentRankingParams, scope: SentimentApiScope): Observable<SentimentApiRankingItem[]> {
    return this.http
      .get<SentimentApiResponse<SentimentApiRankingItem[]>>(this.url('summary/rankings', scope), {
        params: this.buildParams(params, scope).set('type', params.type),
      })
      .pipe(map((response) => response.data ?? []));
  }

  /** Desglose de un elemento del primer panel, que alimenta el segundo. */
  getChildRankings(
    params: SentimentChildRankingParams,
    scope: SentimentApiScope,
  ): Observable<SentimentApiRankingItem[]> {
    const path = `summary/rankings/${params.parentType}/${params.parentId}/${params.childType}`;

    return this.http
      .get<SentimentApiResponse<SentimentApiRankingItem[]>>(this.url(path, scope), {
        params: this.buildParams(params, scope),
      })
      .pipe(map((response) => response.data ?? []));
  }

  /** Menciones del cruce seleccionado; tercer panel de la vista de propiedad. */
  getMentions(params: SentimentCrossParams, scope: SentimentApiScope): Observable<SentimentApiMentionsPage> {
    return this.http
      .get<SentimentApiResponse<SentimentApiMentionsPage>>(this.url('summary/mentions', scope), {
        params: this.withCrossIds(this.buildParams(params, scope), params),
      })
      .pipe(map((response) => response.data ?? { total: 0, items: [] }));
  }

  /**
   * Ranking de las propiedades de la cadena para el cruce seleccionado; tercer panel
   * de la vista corporativa. No tiene equivalente en la vista de propiedad.
   */
  getCustomersRanking(params: SentimentCrossParams): Observable<SentimentApiCustomerRankingItem[]> {
    return this.http
      .get<SentimentApiResponse<SentimentApiCustomerRankingItem[]>>(this.url('summary/customers/ranking', 'chain'), {
        params: this.withCrossIds(this.buildParams(params, 'chain'), params),
      })
      .pipe(map((response) => response.data ?? []));
  }

  /** Página del listado de comentarios. Todo el filtrado ocurre en el servidor. */
  getComments(params: SentimentCommentsParams, scope: SentimentApiScope): Observable<SentimentApiCommentsPage> {
    let httpParams = this.buildParams(params, scope)
      .set('page', params.page)
      .set('size', params.size)
      .set('language_id', params.languageId);

    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.topicIds?.length) {
      httpParams = httpParams.set('topic_ids', params.topicIds.join(','));
    }
    if (params.areaIds?.length) {
      httpParams = httpParams.set('area_ids', params.areaIds.join(','));
    }
    if (params.dimensionIds?.length) {
      httpParams = httpParams.set('dimension_ids', params.dimensionIds.join(','));
    }
    if (params.polarityIds?.length) {
      httpParams = httpParams.set('polarity_ids', params.polarityIds.join(','));
    }

    return this.http
      .get<SentimentApiResponse<SentimentApiCommentsPage>>(this.url('comments', scope), {
        params: httpParams,
      })
      .pipe(
        map(
          (response) =>
            response.data ?? {
              info: [],
              page_number: 0,
              total_pages: 0,
              has_next: false,
              total_of_elements: 0,
              total_mentions_count: 0,
              sentiment_index: null,
            },
        ),
      );
  }

  /**
   * Catálogo de una taxonomía para el panel de filtros. Este endpoint ya acepta la
   * lista completa, así que no distingue alcance.
   */
  getTaxonomy(kind: SentimentTaxonomyKind, customerIds: number[]): Observable<SentimentApiTaxonomyItem[]> {
    return this.http
      .get<SentimentApiResponse<SentimentApiTaxonomyItem[]>>(`${environment.sentimentPath}taxonomy/${kind}`, {
        params: new HttpParams().set('customers_ids', customerIds.join(',')),
      })
      .pipe(map((response) => response.data ?? []));
  }

  /**
   * Planilla Excel de la vista. Vive en el servicio de reportes, no en el de análisis de
   * sentimiento, y responde el archivo plano en lugar del envoltorio `SentimentApiResponse`.
   *
   * El endpoint corporativo no cuelga de una propiedad: recibe las de la cadena en
   * `customers_ids`, como el resto de los reportes de cadena.
   */
  getReport(params: SentimentReportParams, scope: SentimentApiScope): Observable<SentimentReportResponse> {
    const isChain = scope === 'chain';
    const url = isChain
      ? `${environment.reportsPath}reports/sentiment/corporative/${params.type}`
      : `${environment.reportsPath}reports/sentiment/customers/${params.customerIds[0]}/${params.type}`;

    let httpParams = new HttpParams()
      .set('start_date', params.startDate)
      .set('end_date', params.endDate)
      .set('language_id', params.languageId)
      .set('is_async', params.email ? 'true' : 'false')
      .set('email', params.email ?? '');

    if (isChain) {
      httpParams = httpParams.set('customers_ids', params.customerIds.join(','));
    }

    return this.http.get<SentimentReportResponse>(url, { params: httpParams });
  }

  /** Ruta completa de un endpoint, con el prefijo que corresponde al alcance. */
  private url(path: string, scope: SentimentApiScope): string {
    return `${environment.sentimentPath}${SCOPE_CONFIG[scope].prefix}${path}`;
  }

  /** Filtros comunes. Los opcionales se omiten cuando no aplican. */
  private buildParams(params: SentimentSummaryParams, scope: SentimentApiScope): HttpParams {
    let httpParams = new HttpParams()
      .set(SCOPE_CONFIG[scope].customerParam, params.customerIds.join(','))
      .set('start_date', params.startDate)
      .set('end_date', params.endDate);

    if (params.productIds?.length) {
      httpParams = httpParams.set('product_ids', params.productIds.join(','));
    }
    if (params.intensityIds?.length) {
      httpParams = httpParams.set('intensity_ids', params.intensityIds.join(','));
    }
    if (params.otaIds?.length) {
      httpParams = httpParams.set('ota_ids', params.otaIds.join(','));
    }

    return httpParams;
  }

  /** Agrega los ids del cruce que estén presentes; los tres son opcionales. */
  private withCrossIds(httpParams: HttpParams, params: SentimentCrossParams): HttpParams {
    let result = httpParams;

    if (params.topicId) {
      result = result.set('topic_id', params.topicId);
    }
    if (params.areaId) {
      result = result.set('area_id', params.areaId);
    }
    if (params.dimensionId) {
      result = result.set('dimension_id', params.dimensionId);
    }

    return result;
  }
}
